/**
 * Unified analytics — fans out to CloudWatch RUM and PostHog.
 *
 * Call `initializeAnalytics(config)` once at app startup.
 * Then use `trackEvent` / `trackError` everywhere.
 *
 * RUM events are automatically prefixed with "calltree.".
 */

import { AwsRum, AwsRumConfig } from "aws-rum-web";
import posthog from "posthog-js";
import type { AppConfig } from "./config";

const RUM_APPLICATION_VERSION = "1.0.0";

let rum: AwsRum | null = null;
let ph: typeof posthog | null = null;

// ── Initialization ──────────────────────────────────────────────────

function initializeRum(config: AppConfig): void {
  const applicationId = config.rum?.application_id || "";
  const region = config.rum?.region || "us-east-1";

  if (!applicationId) {
    console.log("[Analytics/RUM] No application ID configured, skipping");
    return;
  }

  try {
    const rumConfig: AwsRumConfig = {
      sessionSampleRate: 1,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ["performance", "errors", "http"],
      allowCookies: true,
      enableXRay: false,
      signing: false,
    };
    rum = new AwsRum(applicationId, RUM_APPLICATION_VERSION, region, rumConfig);
    console.log("[Analytics/RUM] Initialized successfully");
  } catch (error) {
    console.error("[Analytics/RUM] Failed to initialize:", error);
  }
}

function initializePostHog(config: AppConfig): void {
  const apiKey = config.analytics?.posthog?.public_api_key || "";

  if (!apiKey) {
    console.log("[Analytics/PostHog] No API key configured, skipping");
    return;
  }

  try {
    ph =
      posthog.init(apiKey, {
        api_host: "https://us.i.posthog.com",
        autocapture: true,
        capture_pageview: true,
        persistence: "localStorage",
      }) ?? null;
    console.log("[Analytics/PostHog] Initialized successfully");
  } catch (error) {
    console.error("[Analytics/PostHog] Failed to initialize:", error);
  }
}

/**
 * Initialize all analytics providers. Idempotent.
 */
export function initializeAnalytics(config: AppConfig): void {
  if (!rum) initializeRum(config);
  if (!ph) initializePostHog(config);
}

// ── Public helpers ──────────────────────────────────────────────────

// Identifying context attached to every event so we can distinguish by
// agent ARN (and contact ID) in PostHog / CloudWatch RUM dashboards.
let identityContext: { agent_arn?: string | null; contact_id?: string | null } = {};

/** Set identifying context that gets merged into every subsequent event.
 *  Patch-style: only the fields you pass are updated; omitted fields keep
 *  their previous value. Lets `/service` set agent_arn once at startup
 *  and incrementally attach contact_id per contact without clearing
 *  agent_arn each time. */
export function setIdentityContext(ctx: { agentArn?: string | null; contactId?: string | null }): void {
  identityContext = {
    ...identityContext,
    ...(ctx.agentArn !== undefined ? { agent_arn: ctx.agentArn } : {}),
    ...(ctx.contactId !== undefined ? { contact_id: ctx.contactId } : {}),
  };
  if (ph) {
    // PostHog: identify by agent ARN so events are attributed to the
    // same person across sessions; also register as super-properties so
    // they appear on every capture.
    if (ctx.agentArn) ph.identify(ctx.agentArn);
    ph.register(identityContext);
  }
  if (rum) {
    // CloudWatch RUM: addSessionAttributes attaches values to all
    // subsequent events in the session. Strip null values — RUM only
    // accepts string | number | boolean.
    const rumAttrs: Record<string, string | number | boolean> = {};
    if (identityContext.agent_arn) rumAttrs.agent_arn = identityContext.agent_arn;
    if (identityContext.contact_id) rumAttrs.contact_id = identityContext.contact_id;
    rum.addSessionAttributes(rumAttrs);
  }
}

export function trackEvent(name: string, properties?: object): void {
  const merged = { ...identityContext, ...(properties ?? {}) };
  if (rum) {
    rum.recordEvent(`calltree.${name}`, merged);
    rum.dispatch();
  }
  if (ph) {
    ph.capture(name, merged);
  }
}

export function trackError(error: Error): void {
  if (rum) {
    rum.recordError(error);
    rum.dispatch();
  }
  if (ph) {
    ph.capture("error", { ...identityContext, message: error.message });
  }
}

